import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, type LazyQueryHookOptions } from '@apollo/client';
// Skillmore UI Components
import usePolling, { POLLING_FETCH_POLICY } from '@empathco/ui-components/src/hooks/usePolling';
// local imports
import { Opportunity, OpportunityQuery, OpportunityQueryVariables } from '../graphql/types';
import { POLLING_ATTEMPTS, POLLING_INTERVAL } from '../config/params';

type ReviewEntryPhase = 'start' | 'sent' | 'inflight' | 'landed' | 'failed';

type ReviewEntry = {
  readonly id: number;
  readonly phase: ReviewEntryPhase;
};

export type OpportunityMatchesPollingParams = {
  readonly opportunityId: number | null | undefined;
  readonly opportunity: Pick<Opportunity, 'id' | 'matches_pending'> | null | undefined;
  readonly active: boolean;
  readonly pending: boolean;
  readonly failed: boolean;
  readonly getOpportunity?: (options?: Partial<LazyQueryHookOptions<OpportunityQuery, OpportunityQueryVariables>>) => void;
};

export type OpportunityMatchesPolling = {
  readonly loading: boolean;
  readonly loadFailed: boolean;
  readonly timedOut: boolean;
  readonly retry: () => void;
};

const startEntry = (id: number | null): ReviewEntry | null => id ? { id, phase: 'start' } : null;

function useReviewEntry(
  entryId: number | null,
  pending: boolean,
  failed: boolean,
  getOpportunity: OpportunityMatchesPollingParams['getOpportunity']
): ReviewEntryPhase | undefined {
  const [entry, setEntry] = useState<ReviewEntry | null>(() => startEntry(entryId));
  if ((entry?.id ?? null) !== entryId) setEntry(startEntry(entryId));
  const phase = entry?.phase;

  useEffect(() => {
    if (!entryId || phase !== 'start') return;
    getOpportunity?.({ variables: { opportunity_id: entryId }, ...POLLING_FETCH_POLICY });
    setEntry({ id: entryId, phase: 'sent' });
  }, [entryId, phase, getOpportunity]);

  useEffect(() => {
    if (!entryId) return;
    if (phase === 'sent' && pending) setEntry({ id: entryId, phase: 'inflight' });
    else if (phase === 'inflight' && !pending) setEntry({ id: entryId, phase: failed ? 'failed' : 'landed' });
  }, [entryId, phase, pending, failed]);

  return phase;
}

function useOpportunityMatchesPolling({
  opportunityId,
  opportunity,
  active,
  pending,
  failed,
  getOpportunity
}: OpportunityMatchesPollingParams): OpportunityMatchesPolling {
  const { cache: apolloCache } = useApolloClient();

  const loaded = Boolean(opportunityId) && opportunityId === opportunity?.id;
  const matchesPending = loaded && Boolean(opportunity?.matches_pending);

  const polled = active && Boolean(opportunityId);
  const entryId = (polled && opportunityId) || null;

  const entryPhase = useReviewEntry(entryId, pending, failed, getOpportunity);
  const entryLanded = !entryId || entryPhase === 'landed';

  const [cappedId, setCappedId] = useState<number | null>();
  const polling = usePolling(
    polled && entryLanded, failed && !loaded, matchesPending && cappedId !== opportunityId, POLLING_INTERVAL
  );

  useEffect(() => {
    if (polling > POLLING_ATTEMPTS) setCappedId(opportunityId);
  }, [polling, opportunityId]);

  useEffect(() => {
    if (!polled) setCappedId(null);
  }, [polled]);

  const [retried, setRetried] = useState(false);
  useEffect(() => {
    if (polling !== 1) setRetried(false);
  }, [polling]);

  const retry = useCallback(() => {
    setCappedId(null);
    setRetried(true);
  }, []);

  const fresh = polling > 1 || (retried && polling === 1);

  useEffect(() => {
    if (!opportunityId || !polled || polling < 1) return;
    getOpportunity?.({ variables: { opportunity_id: opportunityId }, ...fresh ? POLLING_FETCH_POLICY : {} });
  }, [opportunityId, polled, polling, fresh, getOpportunity]);

  const pendingId = useRef<number | null>(null);
  useEffect(() => {
    if (!loaded) return;
    if (matchesPending) pendingId.current = opportunityId || null;
    else if (pendingId.current === opportunityId) {
      pendingId.current = null;
      apolloCache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityMatches' });
      apolloCache.evict({ id: 'ROOT_QUERY', fieldName: 'opportunityBookings' });
    }
  }, [loaded, matchesPending, opportunityId, apolloCache]);

  const mismatch = Boolean(opportunityId) && !loaded;

  return {
    loading: mismatch || !entryLanded,
    loadFailed: (failed && mismatch) || entryPhase === 'failed',
    timedOut: matchesPending && cappedId === opportunityId,
    retry
  };
}

export default useOpportunityMatchesPolling;
